import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SidePanel } from "@/components/ui/side-panel";
import { Textarea } from "@/components/ui/textarea";
import {
  ClientsKeys,
  createReferredClientByStack,
} from "@/features/clients/api/clients";
import {
  AdminFoundersKeys,
  getAdminFounders,
} from "@/features/founders/api/admin-founders";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  licenseeId: z.string().min(1, "Please select a founder"),
  leadName: z.string().min(2, "Lead name must be at least 2 characters"),
  leadEmail: z.string().email("Invalid email address"),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface AdminReferralSubmissionFormProps {
  children?: React.ReactNode;
  preselectedFounderId?: string;
}

const AdminReferralSubmissionForm = ({
  children,
  preselectedFounderId,
}: AdminReferralSubmissionFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      licenseeId: preselectedFounderId || "",
      leadName: "",
      leadEmail: "",
      notes: "",
    },
  });

  // Update form when preselectedFounderId changes
  React.useEffect(() => {
    if (preselectedFounderId && open) {
      form.setValue("licenseeId", preselectedFounderId);
    }
  }, [preselectedFounderId, open, form]);

  // Fetch founders - get all without pagination for the dropdown
  const { data: foundersData, isLoading: isLoadingFounders } = useQuery({
    queryKey: [
      AdminFoundersKeys.GET_FOUNDERS,
      "",
      undefined,
      undefined,
      0,
      1000,
    ],
    queryFn: () => getAdminFounders("", undefined, undefined, 0, 1000),
    enabled: open,
  });

  const createReferralMutation = useMutation({
    mutationFn: (data: FormValues) =>
      createReferredClientByStack({
        licenseeId: data.licenseeId,
        name: data.leadName,
        customerEmail: data.leadEmail,
        notes: data.notes,
      }),
    onSuccess: () => {
      toast({
        title: "Lead referred successfully!",
        description: "The founder will be notified about the new lead.",
      });
      queryClient.invalidateQueries({
        queryKey: [ClientsKeys.ReferredClients],
      });
      queryClient.invalidateQueries({ queryKey: ["referral-stats"] });
      form.reset({
        licenseeId: preselectedFounderId || "",
        leadName: "",
        leadEmail: "",
        notes: "",
      });
      setOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error referring lead",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: FormValues) => {
    createReferralMutation.mutate(values);
  };

  const trigger = children ? (
    <span onClick={() => setOpen(true)} className="inline-flex">
      {children}
    </span>
  ) : (
    <Button className="flex items-center gap-2" onClick={() => setOpen(true)}>
      <UserPlus className="h-4 w-4" />
      Refer Lead
    </Button>
  );

  const footer = (
    <div className="flex justify-end gap-3">
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(false)}
        disabled={createReferralMutation.isPending}
      >
        Cancel
      </Button>
      <Button
        type="button"
        onClick={form.handleSubmit(onSubmit)}
        disabled={createReferralMutation.isPending || !form.formState.isValid}
      >
        {createReferralMutation.isPending ? "Referring Lead..." : "Refer Lead"}
      </Button>
    </div>
  );

  return (
    <>
      {trigger}
      <SidePanel
        open={open}
        onOpenChange={setOpen}
        title="Add New Lead"
        subtitle="Refer a lead to one of the founders."
        footer={footer}
      >
        <Form {...form}>
          <form className="space-y-6">
            <FormField
              control={form.control}
              name="licenseeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assign to Founder</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isLoadingFounders || !!preselectedFounderId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a founder" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingFounders ? (
                        <SelectItem value="loading" disabled>
                          Loading founders...
                        </SelectItem>
                      ) : foundersData?.items?.length === 0 ? (
                        <SelectItem value="empty" disabled>
                          No founders found
                        </SelectItem>
                      ) : (
                        foundersData?.items?.map((founder) => (
                          <SelectItem key={founder._id} value={founder._id}>
                            {founder.firstName} {founder.lastName}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="leadName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lead Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" autoComplete="name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="leadEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lead Email Address</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="john.doe@company.com"
                      type="email"
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes or Context (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any relevant context about the lead, their needs, budget, timeline, etc."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </SidePanel>
    </>
  );
};

export default AdminReferralSubmissionForm;
